import {
  BarPlot,
  LinePlot,
  ChartsXAxis,
  ChartsYAxis,
  ChartsLegend,
  ChartsSurface,
  ChartsTooltip,
} from "@mui/x-charts";
import { ChartDataProviderPro } from "@mui/x-charts-pro/ChartDataProviderPro";
import { useMemo, useRef, useState } from "react";
import { useCfmContext } from "../../../Context/CfmContext";
import { getAgeFromDOB } from "../../../Context/Utils/GetAgeFromDOB";
import "../../../../../../css/CashflowModeller2/Charts/Graphs.css";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import useGraphHeight from "../Utils/useGraphHeight";
import CustomChartWrapper from "../Utils/CustomWrapper";

/**
 * Renders a Surplus/Deficit graph for a selected person, showing income, expenditure, surplus, and deficit over age.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.selectedPerson - The name (or prefix) of the person whose data is displayed.
 * @param {boolean} [props.isPrint=false] - If true, renders the graph in print mode with adjusted dimensions.
 * @param {boolean} [props.animated=true] - If true, enables animation for the chart plots.
 * @param {string} [props.idOverride] - Optional override for the graph's DOM id.
 * @returns {JSX.Element} The rendered Surplus/Deficit graph component.
 *
 * @example
 * <SurplusDeficitGraph selectedPerson="John" isPrint={false} animated={true} />
 *
 * @description
 * This component visualizes the surplus and deficit (as bars) and total expenditure, total income after tax, and target income (as lines)
 * for a selected person, based on their age. It uses context data from Cashflow Modeller and supports print-friendly rendering.
 */
export default function SurplusDeficitGraph({
  selectedPerson,
  isPrint = false,
  animated = true,
  idOverride,
  apiRef = null, // Optional API ref for external control, e.g., printing
}) {
  const { state, dispatch } = useCfmContext();
  const person = state.details?.find((d) => d.name?.startsWith(selectedPerson));
  const currentAge = getAgeFromDOB(person?.dob) ?? 18;
  const chartOnlyRef = useRef();

  const data = useMemo(() => {
    return (
      state.incomeExpenditureResults?.find((entry) =>
        entry.name?.startsWith(selectedPerson)
      )?.incomeExpenditure || []
    );
  }, [state.incomeExpenditureResults, selectedPerson]);

  const { ages, series } = useMemo(() => {
    const filtered = data.filter((row) => row.age >= currentAge);
    const parse = (v) => parseFloat((v || "0").replace(/[^0-9.-]+/g, ""));

    const ages = filtered.map((row) => row.age);

    const surplus = filtered.map((row) => {
      const value = parse(row.surplusDeficit);
      return value >= 0 ? value : 0;
    });

    const deficit = filtered.map((row) => {
      const value = parse(row.surplusDeficit);
      return value < 0 ? value : 0;
    });

    const totalExpenditure = filtered.map((row) => parse(row.totalExpenditure));
    const totalIncomeAfterTax = filtered.map((row) =>
      parse(row.totalIncomeAfterTax)
    );
    const targetIncome = filtered.map((row) => parse(row.targetIncome));

    return {
      ages,
      series: [
        {
          type: "bar",
          data: surplus,
          label: "Surplus",
          color: "#57B5E7",
          stack: "surplus",
          labelMarkType: "circle",
        },
        {
          type: "bar",
          data: deficit,
          label: "Deficit",
          color: "#FC8D62",
          stack: "surplus",
          labelMarkType: "circle",
        },
        {
          type: "line",
          id: "totalExpenditure", // Needed for dashed styling
          data: totalExpenditure,
          label: "Total Expenditure",
          color: "#776096",
          // labelMarkType: "circle",
        },
        {
          type: "line",
          data: totalIncomeAfterTax,
          label: "Total Income After Tax",
          color: "#FBBF72",
        },
        {
          type: "line",
          data: targetIncome,
          label: "Target Income",
          color: "#7AB0D8",
        },
      ],
    };
  }, [data, currentAge]);

  const [activeTab, setActiveTab] = useState("Income & Expenditure");

  const personList = state.details?.map((d) => d.name).filter(Boolean) || [];
  selectedPerson = selectedPerson || personList[0] || "";

  const visibleSeries = series.filter((s) =>
    s.data.some((v) => Number(v ?? 0) !== 0)
  );

  const graphHeight = useGraphHeight(isPrint, true);

  return (
    <div
      ref={chartOnlyRef}
      // style={{ width: isPrint ? 2000 : 1200 }}
      style={{
        width: isPrint ? 800 : "100%",
        height: isPrint ? 500 : "100%",
        backgroundColor: "transparent",
      }}
      id={idOverride || "sur-def-graph"}
      className="cfm-graph-wrapper"
    >
      {/* <Button
        onClick={() => apiRef.current.exportAsImage()}
        variant="contained"
        sx={{ mb: 1 }}
      >
        Print
      </Button> */}
      <ChartDataProviderPro
        apiRef={apiRef}
        key={selectedPerson}
        height={graphHeight}
        series={visibleSeries}
        xAxis={[
          {
            id: "age-axis",
            data: ages,
            scaleType: "band",
            label: "Age",
          },
        ]}
        yAxis={[
          {
            id: "value-axis",
            label: "Amount",
            valueFormatter: (val) => {
              const sign = val < 0 ? "-" : "";
              const absVal = Math.abs(val);
              if (absVal >= 1_000_000)
                return `${sign}£${(absVal / 1_000_000).toFixed(1)}m`;
              if (absVal >= 1_000)
                return `${sign}£${(absVal / 1_000).toFixed(0)}k`;
              return `${sign}£${absVal.toFixed(0)}`;
            },
            width: 70,
          },
        ]}
      >
        <CustomChartWrapper isApiPrint={apiRef ? true : false}>
          <ChartsLegend
            direction="horizontal"
            position="top"
            series={visibleSeries}
            slotProps={{
              legend: {
                sx: {
                  fontSize: 12,
                  color: "#081e56",
                  fontFamily: "'Open Sans'",
                  fontWeight: 500,
                },
              },
            }}
          />
          <ChartsSurface>
            <BarPlot
              animated={animated.toString()}
              springconfig={{ tension: 70, friction: 70 }}
              style={{
                opacity: 0,
                animation: "fadeIn  forwards ease-in-out",
              }}
            />
            <LinePlot
              animated={animated.toString()}
              springconfig={{ tension: 70, friction: 70 }}
              slotProps={{
                line: {
                  style: {
                    strokeWidth: 4,
                  },
                },
              }}
              style={{
                opacity: 0,
                animation: "fadeIn forwards ease-in-out",
              }}
            />

            <ChartsXAxis
              axisId="age-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsYAxis
              axisId="value-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsTooltip />
          </ChartsSurface>
        </CustomChartWrapper>
      </ChartDataProviderPro>
    </div>
  );
};

